// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useState } from "react";
import { Form, Input, InputNumber } from "antd";
import { useDispatch, useSelector } from "react-redux";
import TableContent from "../../components/atoms/TableContent";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import useCurrentPath, {
  deFaultHandlePaginationChange,
  formateDateTime,
  handleDelete,
} from "../../utils/Constants";
import {
  getDiemListThunk,
  deleteDiemThunk,
  addDiemThunk,
  editDiemThunk,
} from "../../store/thunks/diemsThunk";

import media from "../../media";
import EditForm from "../../components/molecules/Forms/editForm";
import { selectedRowInfo } from "../../store/slices/authSlice";

// --------------------------
// REUSABLE CELL COMPONENT
// --------------------------
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber /> : <Input />;
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

// --------------------------
// MAIN COMPONENT
// --------------------------
const Diems = () => {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const currentPath = useCurrentPath();
  const [form] = Form.useForm();
  const [tableForm] = Form.useForm();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const diems = useSelector((state) => state.diems.list) || [];
  const diemsLoading = useSelector(
    (state) => state.diems.loading
  );
  const storePageInfo = useSelector((state) => state.diems.pageInfo);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [storeTotalCount, setStoreTotalCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(true);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const [filteredDiems, setFilteredDiems] = useState([]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDiem, setEditingDiem] = useState(null);

  const [values, setValues] = useState({
    name: "",
    history: "",
    createdAt: "",
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record) => record.key === editingKey;

  // --------------------------
  // API CALLS
  // --------------------------

  // Fetch diems
  const fetchDiems = async () => {
    setLoadingCount(true);
    const res = await dispatch(
      getDiemListThunk({
        route: currentPath,
        pagination: {
          current: 1,
          pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
        },
      })
    ).unwrap();
    setStoreTotalCount(res.totalCount || 0);
    setShowLoader(false);
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    fetchDiems();
  }, [currentPath, dispatch]);

  useEffect(() => {
    if (Array.isArray(diems) && diems.length > 0) {
      setFilteredDiems(diems);
    }
  }, [diems]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const openEditModal = (record) => {
    setEditingDiem(record); // store the record to edit
    tableForm.setFieldsValue({
      level: record.level,
      description: record.description,
      aud: record.aud,
      inr: record.inr,
      gbp: record.gbp,
      usd: record.usd,
    });
    setIsEditModalOpen(true); // open the modal
  };

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    {
      title: "Sr. No.",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Level",
      dataIndex: "level",
      editable: true,
      render: (text) => <>{text}</>,    
    },
    {
      title: "Description",
      dataIndex: "description",
      editable: true,
      render: (text) => <>{text}</>,      
    },
    {
      title: "AUD",
      dataIndex: "aud",
      editable: true,
      render: (text) => <>{text}</>,      
    },
    {
      title: "INR",
      dataIndex: "inr",
      editable: true,
      render: (text) => <>{text}</>,      
    },
    {
      title: "GBP",
      dataIndex: "gbp",
      editable: true,
      render: (text) => <>{text}</>,      
    },
    {
      title: "USD",
      dataIndex: "usd",
      editable: true,
      render: (text) => <>{text}</>,      
    },
    {
      title: "Status",
      dataIndex: "status",
      editable: true,
      className: "all-nowrap-column",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (text) => (
        <>
          <span className={`${text?.toLowerCase()}Status`}>{text}</span>
        </>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (text) => text && formateDateTime(text),
    },
    ...(storeRole === process.env.REACT_APP_SROLE ||
    storeRole === process.env.REACT_APP_ADROLE
      ? [
          {
            title: "Updated At",
            dataIndex: "updatedAt",
            render: (text) => text && formateDateTime(text),
          },
          {
            title: "Action",
            dataIndex: "action",
            render: (_, record) => (
              <span
                className="table-round-btn"
                onClick={() => openEditModal(record)}
              >
                <img src={media.editIcon.svg} alt="Edit icon" />
                <span className="text-size-12 dark-black-color">Edit</span>
              </span>
            ),
          },
        ]
      : []),
  ];

  const mergedColumns =
    columns &&
    columns.map((col) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => ({
          record,
          inputType: "text",
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
        }),
      };
    });

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      const selectedRowKeysChildInfoArr = selectedRows.map((row) => row.key);

      dispatch(
        selectedRowInfo({
          selectedRowKeysChildInfo: selectedRowKeysChildInfoArr,
        })
      );
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  // --------------------------
  // FORM FIELDS
  // --------------------------
  const formFields = [
    {
      isVisible: true,
      label: "Level",
      name: "level",
      rules: [
        { required: true, message: "Please input level!" },
      ],
      type: "input",
      placeholder: "Enter the description",      
      onChange: (e) => {
        setValues({
          ...values,
          description: e.target.value,
        });
      }
    },{
      isVisible: true,
      label: "Description",
      name: "description",
      rules: [
        { required: true, message: "Please input description!" },
      ],
      type: "input",
      placeholder: "Enter the description",      
      onChange: (e) => {
        setValues({
          ...values,
          description: e.target.value,
        });
      }
    },
    {
      isVisible: true,
      label: "AUD",
      name: "aud",
      rules: [],
      type: "input",
      placeholder: "Enter the aud",      
      onChange: (e) => {
        setValues({
          ...values,
          aud: e.target.value,
        });
      }
    },
    {
      isVisible: true,
      label: "INR",
      name: "inr",
      rules: [],
      type: "input",
      placeholder: "Enter the inr",      
      onChange: (e) => {
        setValues({
          ...values,
          inr: e.target.value,
        });
      }
    },
    {
      isVisible: true,
      label: "GBP",
      name: "gbp",
      rules: [],
      type: "input",
      placeholder: "Enter the gbp",      
      onChange: (e) => {
        setValues({
          ...values,
          gbp: e.target.value,
        });
      }
    },
    {
      isVisible: true,
      label: "USD",
      name: "usd",
      rules: [],
      type: "input",
      placeholder: "Enter the usd",      
      onChange: (e) => {
        setValues({
          ...values,
          usd: e.target.value,
        });
      }
    },
  ];

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      <div className="bgi-no-repeat card-stretch rounded-lg p-0">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Diems"
          sectionDescription={
            showLoader ? "Loading..." : `${storeTotalCount} Found`
          }
          hasSectionButton={false}
          hasSearch={true}
          hasSearchButton="general"
          addNewButtonClassName="btn mx-auto px-5 py-2 defaultbutton rounded-lg"
          name="Add New Diem"
          searchClassName="table-search"
          searchPlaceholder="Search Diem's description"
          onButtonClick={() => setIsAddModalOpen(true)}
          searchData={diems}
          onFilteredData={setFilteredDiems}
          searchKeys={["description"]}
          addFormModalOpen={isAddModalOpen}
          addFormSetIsModalOpen={setIsAddModalOpen}
          addFormModalShowLoader={setShowLoader}
          payload={(values) => ({
            level: values.level,
            description: values.description,
            aud: values.aud,
            inr: values.inr,
            gbp: values.gbp,
            usd: values.usd,
            createdAt: new Date().toISOString(),
            historySource: "backend",
            historyAction: "create",
            historyUserID: storeLIUId,
            route: currentPath,
          })}
          list={addDiemThunk}
          formFields={formFields}
          successMessage="Diem added successfully."
          onSuccess={fetchDiems}
          selectedRowKeys={
            storeRole === process.env.REACT_APP_SROLE && selectedRowKeys
          }
          deleteOnClick={() => {
            handleDelete({
              selectedRowKeys,
              dispatch,
              deleteThunk: deleteDiemThunk,
              route: currentPath,
              actionName: "diem",
              onSuccess: () => setSelectedRowKeys([]),
            });
          }}
          searchText={searchText}
          setSearchText={(value) => {
            setShowLoader(true);
            setSearchText(value);
            setTimeout(() => setShowLoader(false), 500);
          }}
        />

        <div
          className={`card p-5 rounded-lg paddingTop-0 ${
            storeTotalCount <= process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE
              ? "paddingBottom-0"
              : "minHeight-100vh"
          }`}
        >
          <div className="table-area">
            <div className="table-responsive">
              <div className="tab-content" id="alltable-size">
                <Form form={tableForm} component={false}>
                  <TableContent
                    bordered={false}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    rowSelection={
                      storeRole === process.env.REACT_APP_SROLE && rowSelection
                    }
                    storeList={filteredDiems}
                    components={{
                      body: {
                        cell: EditableCell,
                      },
                    }}
                    pagination={storePageInfo}
                    loading={showLoader}
                    totalCount={storeTotalCount}
                    handlePaginationChange={(page, pageSize) =>
                      deFaultHandlePaginationChange(
                        (type, pagination) =>
                          dispatch(
                            getDiemListThunk({
                              route: currentPath,
                              pagination,
                            })
                          ),
                        page,
                        pageSize,
                        storeTotalCount
                      )
                    }
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>
        <EditForm
          title="Edit Diem"
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          form={tableForm}
          fields={[
            {
              label: "Level",
              name: "level",
              rules: [
                { required: true, message: "Please input level!" },
              ],
              type: "input",
            },
            {
              label: "Description",
              name: "description",
              rules: [
                { required: true, message: "Please input description!" },
              ],
              type: "input",
            },
            {
              label: "AUD",
              name: "aud",
              rules: [],
              type: "input",
            },
            {
              label: "INR",
              name: "inr",
              rules: [],
              type: "input",
            },
            {
              label: "GBP",
              name: "gbp",
              rules: [],
              type: "input",
            },
            {
              label: "USD",
              name: "usd",
              rules: [],
              type: "input",
            },
          ]}
          submitThunk={editDiemThunk}
          payloadBuilder={(values) => ({
            id: editingDiem.key,       
            level: values.level,     
            description: values.description,
            aud: values.aud,
            inr: values.inr,
            gbp: values.gbp,
            usd: values.usd,
            historySource: "backend",
            historyAction: "update",
            historyUserID: storeLIUId,
          })}
          successMessage="diem updated successfully."
          currentPath={currentPath}
          refreshListThunk={getDiemListThunk}
        />
      </div>
    </div>
  );
};
export default Diems;
